import { Link } from "@tanstack/react-router";
import { CreditCard, WalletCards } from "lucide-react";

interface CreditsExhaustedTurnProps {
  /** Display name of the persona whose turn was refused. */
  personaName: string;
  /** The current workspace's slug — needed for the Subscription link. */
  workspaceSlug?: string;
  /**
   * True for workspace owners and admins — the only roles that can see the
   * workspace credit state (RLS) and act on it from the Subscription page.
   */
  canManageCredits: boolean;
}

/**
 * CR-158: shown in place of an AI reply when the orchestrator refused the turn
 * because the workspace has no AI credits left (402 `credits_exhausted`).
 * Deliberately has NO Retry: a retry can only be refused again until credits
 * are added, which is what the card tells the reader to do.
 */
export function CreditsExhaustedTurn({
  personaName,
  workspaceSlug,
  canManageCredits,
}: CreditsExhaustedTurnProps) {
  const subscriptionSlug = canManageCredits ? workspaceSlug : undefined;

  return (
    <div
      role="alert"
      data-testid="credits-exhausted-turn"
      className="border-destructive/30 bg-destructive/5 my-2 flex items-start gap-3 rounded-lg border px-4 py-3"
    >
      <WalletCards className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-sm font-medium">{personaName} can't answer right now</p>
        <p className="text-muted-foreground mt-0.5 text-xs break-words">
          This workspace has no AI credits left.
        </p>
        {subscriptionSlug ? (
          // The link stays a SIBLING of the sentence: wuchale renders an
          // element nested in mixed text through the catalogue (CR-134).
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-muted-foreground text-xs break-words">
              Add credits on the Subscription page to resume AI.
            </p>
            <Link
              to="/workspaces/$slug/subscription"
              params={{ slug: subscriptionSlug }}
              className="text-foreground inline-flex items-center gap-1 text-xs font-medium underline underline-offset-2"
            >
              <CreditCard className="size-3" aria-hidden />
              Open Subscription
            </Link>
          </div>
        ) : (
          <p
            data-testid="credits-exhausted-ask-owner"
            className="text-muted-foreground mt-1 text-xs break-words"
          >
            Ask a workspace owner to add credits.
          </p>
        )}
      </div>
    </div>
  );
}
